import { useId, useState } from 'react';
import { Link } from 'react-router';

import { type PlanningNature } from '@pfo/domain';

import { instantForTransactionDate, todayInputValue } from '../../../shared/lib/dates.js';
import { toAppError } from '../../../shared/lib/errors.js';
import forms from '../../../shared/styles/forms.module.css';
import {
  buildExpensePayload,
  postExpense,
  updateEntry,
  type ExpenseDraft,
  type QuickAddOptions,
  type SubjectContext,
} from '../data/quickAddRepository.js';
import type { ReviewSubmit } from '../model/reviewSubmit.js';
import { PLANNING_NATURE_LABELS } from '../model/transactionLabels.js';
import { AmountCard } from './AmountCard.js';
import { CategoryPicker } from './CategoryPicker.js';
import { ChipRow } from './ChipRow.js';
import { DatePicker } from './DatePicker.js';
import { missingFieldsMessage } from '../model/missingFields.js';
import { MissingHint } from './MissingHint.js';
import styles from './QuickAddControls.module.css';

export type ExpenseFormInitial = Readonly<{
  amount: number;
  categoryId: string;
  accountId: string;
  date: string;
  note: string;
  forFamily: boolean;
}>;

/**
 * "Chi tiêu" — the screen the whole product is measured by: amount → category → Lưu, in under
 * five seconds (spec v2 22.1).
 *
 * The account comes from preferences and the date defaults to today, so the fast path touches
 * neither. Note, planning nature and "chi cho gia đình" sit behind "Chi tiết".
 *
 * Doubles as the edit form for an existing expense: same fields, `update_entry` instead of
 * `post_entry` (A7 — an entry in an open month is corrected in place).
 *
 * And as the "Sửa" form of a bank notice waiting for approval (`review`, AL1): same fields
 * again, but the draft goes back to the caller, which approves or saves the pending row.
 */
export function ExpenseForm({
  options,
  subjects,
  householdId,
  timeZone,
  isFamily,
  entryId,
  initial,
  review,
  onSaved,
  onCancel,
  onOptionsChanged,
}: {
  options: QuickAddOptions;
  subjects: SubjectContext;
  householdId: string;
  timeZone: string;
  /** The "chi cho gia đình" toggle only exists once there is a family to spend for (A1/A2). */
  isFamily: boolean;
  entryId?: string;
  initial?: ExpenseFormInitial;
  review?: ReviewSubmit<ExpenseDraft>;
  onSaved: (message: string) => void;
  onCancel?: () => void;
  /** Reread the options after a category is created here. */
  onOptionsChanged?: () => Promise<unknown>;
}) {
  const noteId = useId();
  const natureId = useId();

  const [amount, setAmount] = useState<number | null>(initial?.amount ?? null);
  const [categoryId, setCategoryId] = useState(initial?.categoryId ?? '');
  const [account, setAccount] = useState(initial?.accountId ?? options.defaultAccountId ?? '');
  const [date, setDate] = useState(initial?.date ?? (() => todayInputValue(timeZone)));
  const [note, setNote] = useState(initial?.note ?? '');
  const [planningNature, setPlanningNature] = useState<PlanningNature | ''>('');
  const [forFamily, setForFamily] = useState(initial?.forFamily ?? false);

  const [message, setMessage] = useState<string | null>(null);
  const [isSaving, setSaving] = useState(false);

  const canSave = amount !== null && amount > 0 && categoryId !== '' && account !== '';

  async function handleSubmit(action: 'primary' | 'secondary' = 'primary') {
    if (!canSave || amount === null) return;

    setSaving(true);
    setMessage(null);
    try {
      const draft: ExpenseDraft = {
        amount,
        categoryId,
        accountId: account,
        occurredAt: instantForTransactionDate(date, timeZone),
        ...(note.trim() === '' ? {} : { note: note.trim() }),
        ...(planningNature === '' ? {} : { planningNature }),
        ...(forFamily ? { forFamily: true } : {}),
      };

      if (review !== undefined) {
        onSaved(await review.onSubmit(draft, { date, action }));
      } else if (entryId === undefined) {
        await postExpense(options, subjects, draft);
        onSaved('Đã lưu khoản chi.');
      } else {
        await updateEntry(entryId, buildExpensePayload(options, subjects, draft));
        onSaved('Đã cập nhật khoản chi.');
      }
    } catch (error) {
      setMessage(toAppError(error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      className={styles.form}
      onSubmit={(event) => {
        event.preventDefault();
        void handleSubmit();
      }}
      noValidate
    >
      <AmountCard
        value={amount}
        onChange={setAmount}
        autoFocus={entryId === undefined && review === undefined}
      />

      <ChipRow
        label="Trả bằng"
        items={options.accounts.map((item) => ({
          id: item.id,
          label: item.name,
          accountType: item.accountType,
        }))}
        selectedId={account}
        onSelect={setAccount}
        emptyMessage="Chưa có tài khoản nào — thêm ở màn Tài sản."
      />
      {options.defaultAccountId === null && account === '' ? (
        <p className={styles.hint} role="status">
          Chưa có tài khoản mặc định — chọn một tài khoản ở trên, hoặc{' '}
          <Link to="/tai-san">đặt mặc định ở Tài sản</Link> để lần sau không phải chọn.
        </p>
      ) : null}

      <CategoryPicker
        categories={options.categories}
        householdId={householdId}
        selectedId={categoryId}
        onSelect={setCategoryId}
        onCreated={async () => {
          await onOptionsChanged?.();
        }}
      />

      <DatePicker value={date} onChange={setDate} timeZone={timeZone} />

      <details className={forms.advanced}>
        <summary className={forms.summary}>Chi tiết</summary>

        <div className={forms.field}>
          <label htmlFor={noteId}>Ghi chú</label>
          <input
            id={noteId}
            className={forms.input}
            type="text"
            value={note}
            autoComplete="off"
            onChange={(event) => setNote(event.target.value)}
          />
        </div>

        <div className={forms.field}>
          <label htmlFor={natureId}>Tính chất</label>
          <select
            id={natureId}
            className={forms.input}
            value={planningNature}
            onChange={(event) => setPlanningNature(event.target.value as PlanningNature | '')}
          >
            <option value="">Không ghi</option>
            {Object.entries(PLANNING_NATURE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {isFamily ? (
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={forFamily}
              onChange={(event) => setForFamily(event.target.checked)}
            />
            {/* A2: the money still leaves this account, but the expense belongs to the family,
                so it shows as "Chi cho gia đình" instead of a personal category. */}
            Chi cho gia đình
          </label>
        ) : null}
      </details>

      {message ? (
        <p className={forms.error} role="alert">
          {message}
        </p>
      ) : null}

      <div className={styles.footer}>
        {onCancel ? (
          <button type="button" className={forms.secondary} onClick={onCancel}>
            Hủy
          </button>
        ) : null}
        {review !== undefined ? (
          <button
            type="button"
            className={forms.secondary}
            disabled={!canSave || isSaving}
            onClick={() => void handleSubmit('secondary')}
          >
            {review.secondaryLabel}
          </button>
        ) : null}
        <button type="submit" className={styles.save} disabled={!canSave || isSaving}>
          {isSaving
            ? 'Đang lưu…'
            : review !== undefined
              ? review.primaryLabel
              : entryId === undefined
                ? 'Lưu giao dịch'
                : 'Cập nhật'}
        </button>
      </div>
      <MissingHint
        message={missingFieldsMessage([
          (amount === null || amount <= 0) && 'số tiền',
          categoryId === '' && 'danh mục',
          account === '' && 'tài khoản chi',
        ])}
      />
    </form>
  );
}
